import styled from 'styled-components'
import PageHeader from '../../admin/components/PageHeader'
import RoundHistoryTable from '../components/RoundHistoryTable'
import { getPlayerNameFromCookie } from '../store/MetaStore'

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 4px;
`

const StatsContainer = styled.div`
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 20px;
  text-align: center;
  position: relative;

  @media (min-width: 600px) {
    padding: 20px;
  }
`

const GolferNameHeader = styled.div`
  text-align: left;
  margin-bottom: 12px;
  color: #060;
  font-size: 1.4em;
  font-style: italic;
  font-weight: bold;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  align-items: flex-start;

  @media (max-width: 360px) {
    gap: 2px;
  }
`

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
`

const StatLabel = styled.div`
  color: #666;
  font-weight: 600;
  font-size: 0.72rem;
  line-height: 1.1;
  text-align: center;
  margin-bottom: 6px;
  white-space: nowrap;

  @media (min-width: 600px) {
    font-size: 0.9rem;
    margin-bottom: 8px;
  }
`

const HcpBadge = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.$dark ? '#000' : '#060'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  font-weight: bold;
  margin: 0 auto;
  box-shadow: ${props => props.$dark ? '0 2px 4px rgba(0, 0, 0, 0.25)' : '0 2px 4px rgba(0, 102, 0, 0.15)'};

  @media (min-width: 400px) {
    width: 52px;
    height: 52px;
    font-size: 1.15rem;
  }

  @media (min-width: 600px) {
    width: 60px;
    height: 60px;
    font-size: 1.4rem;
  }
`

const GolferItem = styled.div`
  padding: 15px;
  margin-bottom: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #eee;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`

const BackLink = styled.div`
  color: #060;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 15px;
  display: inline-block;
  
  &:hover {
    text-decoration: underline;
  }
`

const GolferInfoView = ({
  golfers,
  selectedGolferId,
  onGolferSelect,
  currentGolfer,
  golferRounds
}) => {
  const validRounds = (golferRounds || []).filter(r => r.grossScore && r.grossScore > 0)
  const avgGross = validRounds.length > 0
    ? (validRounds.reduce((sum, r) => sum + r.grossScore, 0) / validRounds.length).toFixed(1)
    : '-'

  const validNetRounds = (golferRounds || []).filter(r => r.netScore && r.netScore > 0)
  const avgNet = validNetRounds.length > 0
    ? (validNetRounds.reduce((sum, r) => sum + r.netScore, 0) / validNetRounds.length).toFixed(1)
    : '-'

  const currentYear = new Date().getFullYear()
  const isCurrentYear = (round) => {
    const dateStr = round.eventDate || round.roundDate
    if (!dateStr) return false
    if (typeof dateStr === 'string') {
      const match = dateStr.match(/^(\d{4})/)
      if (match) {
        return parseInt(match[1], 10) === currentYear
      }
    }
    const d = new Date(dateStr)
    return !isNaN(d.getTime()) && d.getFullYear() === currentYear
  }

  const currentYearRounds = validRounds.filter(isCurrentYear)
  const currentYearAvgGross = currentYearRounds.length > 0
    ? (currentYearRounds.reduce((sum, r) => sum + r.grossScore, 0) / currentYearRounds.length).toFixed(1)
    : '-'

  const currentYearNetRounds = validNetRounds.filter(isCurrentYear)
  const currentYearAvgNet = currentYearNetRounds.length > 0
    ? (currentYearNetRounds.reduce((sum, r) => sum + r.netScore, 0) / currentYearNetRounds.length).toFixed(1)
    : '-'

  const pageName = getPlayerNameFromCookie() || 'Golfer Info'

  return (
    <>
      <PageHeader pageName={pageName} backTo={selectedGolferId ? null : "/league/menu"} />
      <Container>
        <div className="animate-fade-in" style={{ padding: '0 10px' }}>
          {!selectedGolferId && (
            <>
              {golfers.map(g => (
                <GolferItem key={g.golferId} onClick={() => onGolferSelect(g.golferId)}>
                  <div style={{ fontWeight: 'bold' }}>
                    {g.golferName} <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '6px' }}>({g.hcpIndex})</span>
                  </div>
                  <div style={{ color: '#060' }}>
                    Details ›
                  </div>
                </GolferItem>
              ))}
            </>
          )}

          {currentGolfer && selectedGolferId && (
            <>
              <BackLink onClick={() => onGolferSelect('')}>
                ‹ Back to list
              </BackLink>
              <StatsContainer>
                <GolferNameHeader>
                  {currentGolfer.golferName}
                </GolferNameHeader>
                <StatsGrid>
                  <StatItem>
                    <StatLabel>Hcp Indx</StatLabel>
                    <HcpBadge>{currentGolfer.hcpIndex}</HcpBadge>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Avg Score</StatLabel>
                    <HcpBadge>{avgGross}</HcpBadge>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Avg Net</StatLabel>
                    <HcpBadge>{avgNet}</HcpBadge>
                  </StatItem>
                  <StatItem>
                    <StatLabel>{currentYear} Score</StatLabel>
                    <HcpBadge $dark>{currentYearAvgGross}</HcpBadge>
                  </StatItem>
                  <StatItem>
                    <StatLabel>{currentYear} Net</StatLabel>
                    <HcpBadge $dark>{currentYearAvgNet}</HcpBadge>
                  </StatItem>
                </StatsGrid>
              </StatsContainer>

              <h3 style={{ marginBottom: '15px' }}>Round History</h3>
              <RoundHistoryTable rounds={golferRounds} />
            </>
          )}

          {!currentGolfer && selectedGolferId && (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading stats...</div>
          )}
        </div>
      </Container>
    </>
  )
}

export default GolferInfoView
